'use client';
import React, { useRef, useState, useEffect } from "react";
import Subheading from "@/components/ui/Subheading/Subheading";
import ShiningNumbers from "@/components/ui/ShiningNumber/ShiningNumber";
import { LanguageType, LanguageDirection } from "@/lib/types";
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useAspectRatio from "@/hooks/useAspectRatio";
import { useGSAP } from "@gsap/react";

import { debug } from '@/lib/debug';
import { useIsLg } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger);


interface ValueType {
  heading: string
  paragraph: string
}

interface ValuesSectionProps {
  language?: LanguageType
  subheadingText: string
  paragraphText: string
  values: ValueType[]
  onCardsRefsReady?: (refs: (HTMLDivElement | null)[]) => void
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};


const ValuesSection: React.FC<ValuesSectionProps> = ({
  language = "english",
  subheadingText,
  paragraphText,
  values,
  onCardsRefsReady
}) => {
  const aspectRatio = useAspectRatio();
  const isLg = useIsLg();
  const sectionRef = useRef<HTMLElement>(null);
  const mainHeadingRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const numbersRef = useRef<(HTMLDivElement | null)[]>([]);
  const headingsRef = useRef<(HTMLParagraphElement | null)[]>([]);
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  // Call the callback when refs are ready
  useEffect(() => {
    if (onCardsRefsReady && cardsRef.current.length > 0) {
      onCardsRefsReady(cardsRef.current);
    }
  }, [onCardsRefsReady, values]);

  // Watch for mainHeading size changes and refresh ScrollTrigger
  // Note: This is an element ResizeObserver (not a window listener) — it only fires when
  // the element's own content dimensions change (font swap, text reflow, etc.), NOT on
  // mobile browser chrome appearing/disappearing, so no width-only filtering needed.
  useEffect(() => {
    if (!mainHeadingRef.current) return;

    let refreshTimeout: ReturnType<typeof setTimeout>;
    const resizeObserver = new ResizeObserver(() => {
      // Debounce the refresh to avoid excessive calls
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 150);
    });

    resizeObserver.observe(mainHeadingRef.current);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(refreshTimeout);
    };
  }, []);

  useGSAP(() => {
    if (isLg) {
      const section = sectionRef.current;
      if (!section || !values.length) return;

      const cards = cardsRef.current;
      const numbers = numbersRef.current;
      const headings = headingsRef.current;
      const mainHeading = mainHeadingRef.current;

      // Set initial states
      gsap.set(cards, {
        position: 'relative',
        zIndex: (i) => i,
      });

      if (cards && cards[0]) {
        // Pin the entire section during the card animations
        gsap.timeline({
          scrollTrigger: {
            trigger: mainHeading,
            start: "top top+=32px", // 32px = 2rem  padding top
            end: () => {
              return `+=${(cards.length) * (cards[0]?.offsetHeight ?? 0 + 112) - (mainHeading?.offsetHeight ?? 0)}px`;
            },
            pin: true,
            pinSpacing: false,
            scrub: true,
            // markers: true,
            id: 'values-section-main',
          }
        });
      }

      cards.forEach((card, i) => {
        if (!card || !numbers[i] || !headings[i] || !mainHeading) return;

        // debug.log('mainHeading height ', mainHeading.offsetHeight);

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: card,
            start: () => `top top+=${(mainHeading?.offsetHeight || 0) + 72 + 32}px`, // Use function to get current height
            endTrigger: i < cards.length - 1 ? cards[i + 1] : card,
            end: i < cards.length - 1 ? `+=${(cards.length - i - 1) * (card.offsetHeight + 112)}px` : `+=${0}px`, // 112  = 7 rem => TODO: make this dynamic
            scrub: true,
            pin: i < cards.length - 1,
            pinSpacing: false,
            // markers: true,
            id: `values-section-card-${i}`,
          }
        });

        // If it's not the last card, only animate the paragraph opacity
        if (i < cards.length - 1) {
          tl.to(card.querySelector('p:last-child'), {
            autoAlpha: 0,
            duration: 0.5,
            ease: "none",
            force3D: true,
          });
        }
      });

      // Create separate animation for numbers to control active card index
      numbers.forEach((number, i) => {
        if (!number) return;

        ScrollTrigger.create({
          trigger: number,
          start: "top center",
          end: "bottom center",
          // markers: true,
          id: `values-section-number-${i}`,
          onEnter: () => {
            setActiveCardIndex(i);
          },
          onLeaveBack: () => {
            setActiveCardIndex(Math.max(0, i - 1));
          }
        });
      });
    } else {
      // Mobile intersection observer setup
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const index = cardsRef.current.findIndex(card => card === entry.target);
            if (entry.isIntersecting && index !== -1) {
              setActiveCardIndex(index);
            }
          });
        },
        {
          threshold: 1 // Trigger when card is 100% visible
        }
      );

      // Observe all cards
      cardsRef.current.forEach(card => {
        if (card) observer.observe(card);
      });

      return () => {
        cardsRef.current.forEach(card => {
          if (card) observer.unobserve(card);
        });
      };
    }
  }, { dependencies: [values, isLg, aspectRatio], revertOnUpdate: true });

  return (
    // section has custom padding!
    <section
      style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
      className="
      w-full
      flex flex-col items-center justify-center
      gap-y-[3.5rem] lg:gap-y-[4.5rem]
      px-[1.25rem]
      padding-main-x-desktop
      "
      ref={sectionRef}>
      <div
        ref={mainHeadingRef}
        className="
        w-full
        container-large
        relative
        z-10
        ">
        <div
          className="
            w-full
            flex flex-col lg:flex-row lg:flex-wrap lg:items-end gap-x-[1rem]
            gap-y-[2rem] lg:gap-y-[3.5rem]
          ">
          <div style={{ direction: "ltr" }} className="w-full lg:w-[18rem] lg:flex-grow lg:max-w-[24.3125rem]">
            <Subheading
              size={isLg ? 24 : 20}
              alignment={language === "english" ? 'left' : 'right'}
              subheading={subheadingText}
            />
          </div>

          <div className="w-full lg:flex-grow lg:w-[30.6875rem] tb:max-w-[37rem]">
            <p className={`
            ${language === 'arabic' ?
                'text-right'
                :
                'text-left'
              }
            text-text-tertiary
            text-20--alexandria
              }
            `}>
              {paragraphText}
            </p>
          </div>

        </div>

        {/* Move the image to be positioned relative to mainHeading bottom */}
        <div
        style={{
          transform: `translateY(calc(+50% + 4.5rem))`
        }}
         className={`
          absolute bottom-0
          ${language === "english" ? 'right-[6px] lg:right-[90px]' : 'left-[6px] lg:left-[90px]'}
        `}>
          <img src="/Support-Badge-Filled.svg" alt="Support lockup clean badge"
            style={{
              zIndex: 9999,
              height: 'clamp(4.5rem, 2.2421rem + 4.71vw, 7.75rem)'
            }}
          />
        </div>
      </div>

      <div className="
        w-full
        container-x-large
        ">
        <div className="
          w-full
          flex flex-col
          gap-y-[2.5rem]
          lg:gap-y-[7rem]
        ">
          {
            values && values.map((value: ValueType, index: number) => (
              <div
                key={index}
                ref={el => {
                  cardsRef.current[index] = el;
                }}
                // style={{ backgroundColor }}
                // bg-bg-light-primary
                // before:absolute before:inset-0 before:bg-transparent/80 before:-z-[1]

                // relative
                // before:absolute before:inset-0 before:bg-transparent/80 before:-z-[1]
                className="
                  w-full
                  flex flex-col justify-center items-center
                  gap-y-[3.5rem]
                  lg:gap-y-[6rem]
                ">
                <div className="w-full relative h-[1.5px] bg-stroke-soft-200">
                  {/* Image moved to mainHeadingRef - no longer needed here */}
                </div>

                <div className="
                  w-full
                  container-large
                  flex flex-col lg:flex-row lg:flex-wrap
                  gap-y-[1.5rem] lg:gap-x-[1rem] lg:gap-y-[4.5rem]
                  ">
                  <div
                    ref={el => {
                      numbersRef.current[index] = el;
                    }}
                    className="w-full lg:w-[18rem] lg:max-w-[24.3125rem] lg:flex-grow"
                  >
                    <ShiningNumbers
                      size={isLg ? 76 : 44}
                      number={"0" + (index + 1)}
                      active={index === activeCardIndex}
                    />
                  </div>
                  <div className="
                    w-full
                    lg:w-[37rem] tb:max-w-[49.6875rem] lg:flex-grow
                    ">
                    <h3
                      ref={el => {
                        headingsRef.current[index] = el;
                      }}
                      className={`
                      w-full
                      pb-[1.5rem] lg:pb-[4.5rem]
                      ${language === 'arabic' ?
                          'text-right'
                          :
                          'text-left'
                        }
                      text-text-primary
                      text-52-to-24--neue-june
                      `}>
                      {value.heading}
                    </h3>
                    <p className={`
                      w-full tb:max-w-[43.3125rem]
                      ${language === 'arabic' ?
                        'text-right'
                        :
                        'text-left'
                      }
                      text-text-tertiary
                      text-20--alexandria
                      }
                    `}>
                      {value.paragraph}
                    </p>
                  </div>
                </div>
              </div>
            ))

          }

        </div>
      </div>
    </section>
  );
}


export default ValuesSection;
